import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, CheckCircle, AlertCircle, X, Loader2, ClipboardList } from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const ALLOWED_EXTS = ['.txt', '.docx', '.pdf'];
const MAX_SIZE_MB = 10;

// ─── Sentiment config (matches Messages.js) ───────────────────────────────────

const sentimentConfig = {
  very_positive: { color: 'text-[#2e4d6e]', bg: 'bg-[#395b81]/10 border border-[#395b81]/30', label: 'Very Positive' },
  positive:      { color: 'text-[#547a84]', bg: 'bg-[#547a84]/10 border border-[#547a84]/30', label: 'Positive' },
  neutral:       { color: 'text-[#7a7a8a]', bg: 'bg-[#38394e]/8 border border-[#38394e]/15',  label: 'Neutral' },
  negative:      { color: 'text-[#c06a55]', bg: 'bg-[#f0856d]/10 border border-[#f0856d]/30', label: 'Negative' },
  very_negative: { color: 'text-[#a0442e]', bg: 'bg-[#f0856d]/20 border border-[#f0856d]/40', label: 'Very Negative' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatAgo(date) {
  const ms = Date.now() - new Date(date).getTime();
  const mins = Math.floor(ms / 60000);
  const hrs  = Math.floor(ms / 3600000);
  const days = Math.floor(ms / 86400000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs  < 24) return `${hrs}h ago`;
  return `${days}d ago`;
}

// ─── Main component ───────────────────────────────────────────────────────────

function MeetingNotes() {
  const navigate = useNavigate();

  // Upload state
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Recent notes state
  const [recentNotes, setRecentNotes] = useState([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(true);

  useEffect(() => {
    fetchRecentNotes();
  }, []);

  const fetchRecentNotes = async () => {
    setIsLoadingRecent(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/messages?channel=meeting_note`);
      if (res.data.success) {
        setRecentNotes(res.data.messages.slice(0, 10));
      }
    } catch (err) {
      console.error('Error fetching recent notes:', err);
    } finally {
      setIsLoadingRecent(false);
    }
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    setError(null);

    const ext = '.' + selected.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      setError(`Unsupported file type. Please upload ${ALLOWED_EXTS.join(', ')}`);
      return;
    }
    if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`File too large. Maximum size is ${MAX_SIZE_MB}MB.`);
      return;
    }

    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setIsUploading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    if (title.trim()) formData.append('title', title.trim());

    try {
      const token = localStorage.getItem('pulsoToken');
      const res = await axios.post(`${API_URL}/api/admin/meeting-notes`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.data.success) {
        setResult(res.data.data);
        setFile(null);
        setTitle('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchRecentNotes(); // refresh the list
      } else {
        setError(res.data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setTitle('');
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Meeting Notes</h1>
          <p className="text-muted-foreground">
            Upload a meeting note — Claude analyses it for themes and sentiment, and it feeds into the dashboard
          </p>
        </div>

        {/* Upload Card */}
        <div className="rounded-lg border border-border bg-card shadow-sm p-6">
          {result ? (
            /* ── Success State ── */
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#547a84] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground">Meeting note processed</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Saved as a message and added to the dashboard.
                  </p>
                </div>
              </div>

              <div className="rounded-md bg-muted/30 border border-border p-4 space-y-3">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Themes</span>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {(result.themes || []).map(t => (
                      <span key={t} className="inline-flex items-center rounded-full bg-[#395b81]/10 border border-[#395b81]/30 px-2.5 py-0.5 text-xs font-medium text-[#395b81] capitalize">
                        {t.replace(/-/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sentiment</span>
                  <div className="mt-1.5">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${sentimentConfig[result.sentiment]?.bg || ''} ${sentimentConfig[result.sentiment]?.color || ''}`}>
                      {sentimentConfig[result.sentiment]?.label || result.sentiment}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={reset}
                  className="inline-flex items-center gap-2 rounded-md bg-[#f0856d] text-white px-4 py-2 text-sm font-medium hover:bg-[#e07458] transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  Upload another
                </button>
                <button
                  onClick={() => navigate('/messages')}
                  className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  View in Messages
                </button>
              </div>
            </div>
          ) : (
            /* ── Upload State ── */
            <div className="space-y-4">
              {/* File picker */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Meeting note file
                </label>
                {file ? (
                  <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-4 py-3">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-[#395b81]" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
                        <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors flex-shrink-0"
                      aria-label="Remove file"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 rounded-md border-2 border-dashed border-border bg-muted/20 cursor-pointer hover:bg-muted/40 transition-colors">
                    <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                    <span className="text-sm font-medium text-foreground">Click to choose a file</span>
                    <span className="text-xs text-muted-foreground mt-1">.txt, .docx, or .pdf — up to {MAX_SIZE_MB}MB</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".txt,.docx,.pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Title input */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Title <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Ward 5 community meeting, May 29"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#f0856d]/40 focus:border-[#f0856d]/40"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Helps you identify this note later. Defaults to the filename if left blank.
                </p>
              </div>

              {/* Error display */}
              {error && (
                <div className="flex items-start gap-2 rounded-md bg-[#f0856d]/10 border border-[#f0856d]/30 px-3 py-2">
                  <AlertCircle className="h-4 w-4 text-[#c06a55] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#c06a55]">{error}</p>
                </div>
              )}

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={!file || isUploading}
                className="inline-flex items-center gap-2 rounded-md bg-[#f0856d] text-white px-4 py-2 text-sm font-medium hover:bg-[#e07458] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analysing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload & analyse
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Recent uploads */}
        <div className="rounded-lg border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <h3 className="font-semibold text-foreground">Recent meeting notes</h3>
            {recentNotes.length > 0 && (
              <span className="text-xs text-muted-foreground">{recentNotes.length} shown</span>
            )}
          </div>

          {isLoadingRecent ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading...</div>
          ) : recentNotes.length === 0 ? (
            <div className="p-10 text-center">
              <ClipboardList className="h-10 w-10 text-muted-foreground/60 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No meeting notes uploaded yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Title</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Uploaded by</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Themes</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sentiment</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">When</th>
                  </tr>
                </thead>
                <tbody>
                  {recentNotes.map(note => (
                    <tr
                      key={note._id}
                      className="border-b border-border hover:bg-muted/20 transition-colors cursor-pointer"
                      onClick={() => navigate(`/respond/${note._id}`)}
                    >
                      <td className="p-3">
                        <div className="text-sm font-medium text-foreground">
                          {note.metadata?.title || note.metadata?.originalFilename || 'Untitled'}
                        </div>
                        {note.metadata?.originalFilename && note.metadata?.title && (
                          <div className="text-xs text-muted-foreground truncate max-w-[300px]">{note.metadata.originalFilename}</div>
                        )}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">{note.senderName || '—'}</td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {(note.themes || []).slice(0, 2).map(t => (
                            <span key={t} className="inline-flex items-center rounded-full bg-[#395b81]/10 border border-[#395b81]/30 px-2 py-0.5 text-xs text-[#395b81] capitalize">
                              {t.replace(/-/g, ' ')}
                            </span>
                          ))}
                          {(note.themes || []).length > 2 && (
                            <span className="text-xs text-muted-foreground self-center">+{note.themes.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        {note.sentiment && (
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${sentimentConfig[note.sentiment]?.bg || ''} ${sentimentConfig[note.sentiment]?.color || ''}`}>
                            {sentimentConfig[note.sentiment]?.label || note.sentiment}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground whitespace-nowrap">
                        {formatAgo(note.receivedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </DashboardLayout>
  );
}

export default MeetingNotes;